import React, { useMemo, useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { search } from '../../store/ducks/search/actions';
import { imgUrl } from '../../config/config';
import { getSearchPage } from '../../store/ducks/searchPage/actions';

import './Search.scss';
import { SearchFilters } from './components/SearchFilters';
import { SearchResults } from './components/SearchResults';
import { SearchPagination } from './components/SearchPagination';
import { SearchLoader } from './components/SearchLoader';
import { useRemarkSync } from 'react-remark';
import { filtersList, searchNewsId, searchCarierId, searchSingleProductId, searchProductsId, searchTermsId, searchPrivacyId, searchCookiesId, searchAboutId, searchHomeId } from '../../store/ducks/search/config';
import { Link } from 'react-router-dom';
import { LangContext } from '../../components/App/App';

type SearchItem = {
    Title?: string;
    Date?: string;
    Reference?: string | null;
    Slug?: string | number | null;
    ShareCaption?: string | null;
    contentTypeUid?: string;
};

type RootState = {
    searchReducer: {
        status: string;
        items: SearchItem[] | null | undefined;
    };
    searchPageReducer: {
        status: string;
        data: any | null;
    };
};

const PAGE_SIZE = 6;

const Search = () => {
    const dispatch = useDispatch();

    const [query, setQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'all' | 'news' | 'catalog' | 'pages'>('all');
    const [page, setPage] = useState(1);
    const [showDropdown, setShowDropdown] = useState(false);

    const status = useSelector((state: RootState) => state.searchReducer.status);
    const items = useSelector((state: RootState) => state.searchReducer.items);
    const searchPageStatus = useSelector((state: RootState) => state.searchPageReducer.status);
    const searchPageData = useSelector((state: RootState) => state.searchPageReducer.data);

    const lang = useContext(LangContext);

    useEffect(() => {
        if (searchPageStatus === 'IDLE') {
            dispatch(getSearchPage({}));
        }
    }, [searchPageStatus, dispatch]);

    const allItems: SearchItem[] = useMemo(() => {
        if (!Array.isArray(items)) {
            return [];
        }
        return items;
    }, [items]);

    const categorized = useMemo(() => {
        const news = allItems.filter(i => {
            const uid = i.contentTypeUid;
            return uid && filtersList[uid] === 'news';
        });

        const catalog = allItems.filter(i => {
            const uid = i.contentTypeUid;
            return uid && filtersList[uid] === 'catalog';
        });

        const pages = allItems.filter(i => {
            const uid = i.contentTypeUid;
            return uid && filtersList[uid] === 'pages';
        });

        return {
            all: allItems,
            news,
            catalog,
            pages,
        };
    }, [allItems]);

    const counts = useMemo(() => ({
        all: categorized.all.length,
        news: categorized.news.length,
        catalog: categorized.catalog.length,
        pages: categorized.pages.length,
    }), [categorized]);

    const currentList: SearchItem[] = useMemo(() => {
        switch (activeTab) {
            case 'news':
                return categorized.news;
            case 'catalog':
                return categorized.catalog;
            case 'pages':
                return categorized.pages;
            default:
                return categorized.all;
        }
    }, [activeTab, categorized]);

    const totalPages = useMemo(() => {
        if (currentList.length === 0) {
            return 1;
        }
        return Math.ceil(currentList.length / PAGE_SIZE);
    }, [currentList.length]);

    const pagedItems = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        return currentList.slice(start, end);
    }, [currentList, page]);

    const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
        e.preventDefault();
        if (!query) {
            return;
        }
        setPage(1);
        setShowDropdown(true);
        dispatch(search({ searchString: query, from: 0, pagesize: 100, lang: lang && lang.lang && lang.lang.value }));
    };

    const handleClear = () => {
        console.log('handleClear');
        setQuery('');
        setShowDropdown(false);
    };

    const handleSelectTab = (tab: 'all' | 'news' | 'catalog' | 'pages') => {
        if (tab === activeTab) {
            return;
        }
        setActiveTab(tab);
        setPage(1);
    };

    const handlePrev = () => {
        if (page <= 1) {
            return;
        }
        setPage(prev => prev - 1);
    };

    const handleNext = () => {
        if (page >= totalPages) {
            return;
        }
        setPage(prev => prev + 1);
    };

    const pageIndexes = useMemo(() => {
        const total = totalPages;
        const indexes: number[] = [];
        const maxHead = Math.min(6, total);
        for (let i = 0; i < maxHead; i += 1) {
            indexes.push(i);
        }
        const lastIndex = total - 1;
        if (lastIndex >= 0 && lastIndex >= maxHead) {
            indexes.push(lastIndex);
        }
        return indexes;
    }, [totalPages]);

    const getItemLink = (item: SearchItem): string | null => {
        const uid = item.contentTypeUid;
        const slug = item.Slug;

        if (!uid) {
            return null;
        }

        if (uid === searchSingleProductId) {
            return `/products/${slug}`;
        }

        if (uid === searchProductsId) {
            return `/products`;
        }

        if (uid === searchNewsId) {
            return `/post/${slug}`;
        }

        if (uid === searchCarierId) {
            return '/freight';
        }

        if (uid === searchNewsId) {
            return `/post/${slug}`;
        }

        if (uid === searchTermsId) {
            return `/policies/terms-and-conditions`;
        }

        if (uid === searchPrivacyId) {
            return `/policies/privacy-policy`;
        }

        if (uid === searchCookiesId) {
            return `/policies/cookies-policy`;
        }

        if (uid === searchAboutId) {
            return `/about`;
        }

        if (uid === searchHomeId) {
            return `/`;
        }

        return null;
    };

    return (
        <section className="search">
            <div className="container search-container">
                <div className="search-wrapper">
                    <form className="search-form" onSubmit={handleSubmit} role="search">
                        <div className="search-form__field">
                            <input
                                type="text"
                                name="search-form"
                                placeholder={(searchPageData && searchPageData.Placeholder) || 'Search'}
                                onChange={(e) => setQuery(e.target.value)}
                                value={query}
                            />
                            <button
                                type="button"
                                className="clear-field"
                                onClick={handleClear}
                                aria-label={'Clear search'}
                            >
                                <img src={imgUrl + 'close.svg'} alt="clear" />
                            </button>
                        </div>
                        {showDropdown && status === 'SUCCESS' && allItems.length > 0 && (
                            <div className="search-dropdown" role="listbox">
                                {allItems.slice(0, 3).map((item) => (
                                    <Link to={getItemLink(item)} key={item.Title} className="search-dropdown__item" role="option">

                                        {useRemarkSync(item.Title)}
                                    </Link>
                                ))}
                                <div className="search-dropdown__item">
                                    <button
                                        type="button"
                                        onClick={() => setShowDropdown(false)}
                                    >
                                        <strong>{(searchPageData && searchPageData.See_all) || 'See all results'}</strong>
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>

                    {status === 'LOADING' && (
                        <SearchLoader/>
                    )}

                    {status === 'SUCCESS' && !showDropdown && (
                        <SearchFilters
                          activeTab={activeTab}
                          counts={counts}
                          handleSelectTab={handleSelectTab}
                          labels={{
                            all: searchPageData  && searchPageData.Allcontent,
                            news: searchPageData  && searchPageData.News,
                            catalog: searchPageData &&  searchPageData.Catalog,
                            pages: searchPageData && searchPageData.Pages,
                          }}
                        />
                    )}

                    {status === 'SUCCESS' && !showDropdown && (
                        <SearchResults
                          status={status}
                          counts={counts}
                          items={pagedItems}
                          getItemLink={getItemLink}
                          labels={{
                            empty: (searchPageData && searchPageData.No_results) || 'No results found',
                          }}
                        />
                    )}

                    {status === 'SUCCESS' && counts.all > 0 && !showDropdown && (
                        <SearchPagination
                            page={page}
                            totalPages={totalPages}
                            pageIndexes={pageIndexes}
                            onPrev={handlePrev}
                            onNext={handleNext}
                            onPageSelect={setPage}
                            labels={{
                              prev: (searchPageData && searchPageData.Prev) || 'Prev',
                              next: (searchPageData  && searchPageData.Next) || 'Next',
                            }}
                        />
                    )}
                    {status === 'SUCCESS' && counts.all === 0 && (
                        <>
                        <div className="search-empty">{searchPageData && searchPageData.No_results || 'No results found'}</div>
                        <div className="search-empty__text">{searchPageData && searchPageData.Try_again || ''}</div>
                        </>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Search;
